import { useCallback, useEffect, useRef, useState } from 'react';

import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { AlertWithReferences } from '@/components/atoms/AlertWithReferences/AlertWithReferences';
import { WarningAlert } from '@/components/atoms/WarningAlert/WarningAlert';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { useAllRules } from '@/hooks/useAllRules';
import useBrains from '@/hooks/useBrains';
import { useRunnableCampaignsByBrain } from '@/modules/campaigns/hooks/useCampaigns';
import { getCampaignDetailPage } from '@/modules/campaigns/util/getCampaignDetailPage';
import { popModal } from '@/redux/modals/actions';
import { selectAccountSlug } from '@/redux/session/selectors';
import { trackEvent } from '@/segment/segment';
import { Campaign } from '@/shared/types/campaigns';
import { Rule } from '@/shared/types/rules';
import { EventName } from '@/shared/types/segment';
import { isKeyEnter } from '@/util/util';

import ModalDelete from './ModalDelete';
import ModalWarn from './ModalWarn';
type Props = { brainName: string; brainId: string; onDeleted?: () => void };

const ModalBrainDelete = ({ brainName, brainId, onDeleted }: Props) => {
  const navigate = useNavigate();
  const { deleteBrain, activeSessions, deleteStatus } = useBrains(brainId);
  const slug = useSelector(selectAccountSlug);
  const dispatch = useDispatch();
  const { rulesConnected, noRules } = useAllRules({ brainId });
  const { t } = useTranslation();
  const rules = !noRules;

  const {
    campaigns: campaignsConnected,
    isLoading: campaignsLoading,
    isUnchecked: campaignsUnchecked,
  } = useRunnableCampaignsByBrain(brainId);
  const [endActiveSessions, setEndActiveSessions] = useState(false);
  const [deleteText, setDeleteText] = useState('');
  const deleteInputRef = useRef<HTMLInputElement>(null);

  // Focus the input field when it appears after checking the checkbox
  useEffect(() => {
    if (endActiveSessions && deleteInputRef.current) {
      deleteInputRef.current.focus();
    }
  }, [endActiveSessions]);

  const isDeleting = deleteStatus === 'pending';

  const handleDelete = useCallback(() => {
    deleteBrain(
      { brainId, options: endActiveSessions ? { force: true } : undefined },
      {
        onSuccess: () => {
          if (onDeleted) {
            onDeleted();
          }
        },
        onSettled: () => {
          dispatch(popModal());
        },
      }
    );
    trackEvent(EventName.ClickDeleteBrain, { brain_id: brainId });
  }, [brainId, deleteBrain, dispatch, onDeleted, endActiveSessions]);

  const subtitle = <Trans i18nKey="brains.delete_warning_ai_agent" values={[brainName]} />;

  const sessionsSubtitle = (
    <Trans
      i18nKey="brains.delete_warning_end_active_sessions_description"
      values={{ 0: activeSessions }}
    />
  );

  // Handlers for the active sessions flow
  const isDeleteButtonEnabled =
    endActiveSessions && deleteText.toLowerCase() === 'delete' && !isDeleting && !campaignsLoading;

  const handleDeleteConfirm = useCallback(() => {
    if (endActiveSessions) {
      handleDelete();
    }
  }, [endActiveSessions, handleDelete]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (isKeyEnter(event) && isDeleteButtonEnabled) {
        event.preventDefault();
        handleDeleteConfirm();
      }
    },
    [isDeleteButtonEnabled, handleDeleteConfirm]
  );

  if (rules) {
    return (
      <ModalWarn>
        <AlertWithReferences<Partial<Rule>>
          referenceProp="rule_id"
          references={rulesConnected}
          labelKey="name"
          onRefClick={(rule_id) => {
            const deskId = rulesConnected.find((rule) => rule.rule_id === rule_id)?.desk_id;
            dispatch(popModal());
            navigate(`/${slug}/environments/${deskId}/rules/${rule_id}`);
          }}
        >
          <Trans i18nKey={'brains.delete_warning_not_empty'} values={{ 0: brainName }} />
        </AlertWithReferences>
      </ModalWarn>
    );
  }

  if (campaignsConnected.length > 0) {
    return (
      <ModalWarn>
        <AlertWithReferences<Partial<Campaign>>
          referenceProp="campaign_id"
          references={campaignsConnected}
          labelKey="name"
          onRefClick={(campaignId) => {
            const status = campaignsConnected.find(
              (campaign) => campaign.campaign_id === campaignId
            )?.status;
            dispatch(popModal());
            navigate(`/${slug}/campaigns/${campaignId}/${getCampaignDetailPage(status)}`);
          }}
        >
          <Trans i18nKey={'brains.delete_warning_campaigns'} values={{ 0: brainName }} />
        </AlertWithReferences>
      </ModalWarn>
    );
  }

  if (campaignsUnchecked) {
    return (
      <ModalWarn>
        <WarningAlert>
          <p>
            <Trans
              i18nKey={'brains.delete_warning_campaigns_unchecked'}
              values={{ 0: brainName }}
            />
          </p>
        </WarningAlert>
      </ModalWarn>
    );
  }

  if (activeSessions) {
    return (
      <AlertDialog
        open
        onOpenChange={(open) => {
          if (!open) {
            dispatch(popModal());
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('brains.delete_warning_new_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('brains.delete_warning_new_description', { 0: brainName })}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="endSessions"
                  checked={endActiveSessions}
                  onCheckedChange={(checked) => setEndActiveSessions(checked as boolean)}
                />
                <label
                  htmlFor="endSessions"
                  className="text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {t('brains.delete_warning_end_active_sessions')}
                </label>
              </div>
              <div className="mt-1 ml-6 text-xs text-muted-foreground">{sessionsSubtitle}</div>
            </div>

            {endActiveSessions && (
              <div>
                <div className="mb-2 text-sm">{subtitle}</div>
                <Input
                  ref={deleteInputRef}
                  value={deleteText}
                  onChange={(e) => setDeleteText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t('common.delete')}
                />
              </div>
            )}
          </div>

          <AlertDialogFooter>
            <Button variant="ghost-neutral" onClick={() => dispatch(popModal())}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              disabled={!isDeleteButtonEnabled}
              variant="destructive"
              isLoading={isDeleting}
            >
              {t('common.delete')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  if (!rules) {
    return (
      <ModalDelete
        subtitle={subtitle}
        confirm
        onDelete={handleDelete}
        primaryDisabled={campaignsLoading}
      />
    );
  }
};

export default ModalBrainDelete;
